import { useEffect, useState } from 'react'
import { AlertTriangle, Clock, Crown, X } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useAuth } from '@/hooks/use-auth'
import { paymentService, SubscriptionStatus } from '@/services/payment.service'

interface TrialBannerProps {
  onUpgrade?: () => void
}

export function TrialBanner({ onUpgrade }: TrialBannerProps) {
  const { user } = useAuth()
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (user?.is_freemium_user) {
      loadSubscriptionStatus()
    }
  }, [user])

  const loadSubscriptionStatus = async () => {
    try {
      const status = await paymentService.getSubscriptionStatus()
      setSubscription(status)

      if (status.has_subscription && status.subscription?.trial_ends_at) {
        const trialEnd = new Date(status.subscription.trial_ends_at)
        const now = new Date()
        const diffTime = trialEnd.getTime() - now.getTime()
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        setDaysRemaining(diffDays)
      }
    } catch (error) {
      console.error('[TrialBanner] Failed to load subscription status:', error)
    }
  }

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade()
    } else {
      window.location.href = '/app/pricing'
    }
  }

  // Hide for non-freemium users, dismissed, or school-affiliated staff (school pays for them)
  const isSchoolStaff = user?.school_id && user?.primary_role !== 'school_admin' && user?.primary_role !== 'admin'
  if (!user?.is_freemium_user || dismissed || isSchoolStaff) {
    return null
  }

  // Trial expired
  if (daysRemaining !== null && daysRemaining <= 0) {
    return (
      <Alert className="border-red-500 bg-red-50">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <AlertDescription className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-red-800">Your trial has expired!</p>
            <p className="text-sm text-red-700 mt-1">
              Upgrade now to continue accessing premium features and keep your progress.
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Button
              onClick={handleUpgrade}
              size="sm"
              className="bg-red-600 hover:bg-red-700"
            >
              <Crown className="mr-2 h-4 w-4" />
              Upgrade Now
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    )
  }

  // Trial ending soon (3 days or less)
  if (daysRemaining !== null && daysRemaining <= 3 && daysRemaining > 0) {
    const progress = ((3 - daysRemaining) / 3) * 100

    return (
      <Alert className="border-orange-500 bg-orange-50">
        <Clock className="h-4 w-4 text-orange-600" />
        <AlertDescription>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="font-semibold text-orange-800">
                Only {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left in your trial!
              </p>
              <p className="text-sm text-orange-700 mt-1">
                Upgrade now to avoid losing access to premium features.
              </p>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Button
                onClick={handleUpgrade}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Crown className="mr-2 h-4 w-4" />
                Upgrade
              </Button>
              <Button
                onClick={() => setDismissed(true)}
                size="sm"
                variant="ghost"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Progress value={progress} className="h-2 bg-orange-200" />
        </AlertDescription>
      </Alert>
    )
  }

  // Active trial with time remaining
  if (
    daysRemaining !== null &&
    daysRemaining > 3 &&
    subscription?.has_subscription &&
    subscription.subscription?.is_trial
  ) {
    return (
      <Alert className="border-blue-500 bg-blue-50">
        <Clock className="h-4 w-4 text-blue-600" />
        <AlertDescription className="flex items-center justify-between">
          <div>
            <p className="font-medium text-blue-800">
              🎉 Trial active: {daysRemaining} days remaining
            </p>
            <p className="text-sm text-blue-700 mt-1">
              Enjoying premium features? Upgrade anytime to keep them forever.
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Button
              onClick={handleUpgrade}
              size="sm"
              variant="outline"
              className="border-blue-600 text-blue-700 hover:bg-blue-100"
            >
              View Plans
            </Button>
            <Button
              onClick={() => setDismissed(true)}
              size="sm"
              variant="ghost"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    )
  }

  return null
}
